import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { ListItem as ListItemMUI, makeStyles } from '@material-ui/core';
import { ArrowDropUp, ArrowDropDown } from '@material-ui/icons';

import { KeyboardShortcut } from 'components';
import { KeyboardShortcutsToggle } from 'components';
import { getLocalSettingsProxy } from 'utilities';
import { NAVIGATION_BUTTONS } from 'constants';
import { useKeyboardKeyLabels } from 'hooks';

const useStyles = makeStyles((theme) => {
  return {
    keyboardShortcutsMenu: () => {
      return {
        padding: theme.spacing(2),
        margin: 0,
        overflowY: 'auto',
      };
    },
    togglePlaceholder: {
      padding: theme.spacing(1, 0, 3),
      overflow: 'hidden',
    },
    keyboardShortcuts: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      ['@media (max-width:370px)']: {
        gap: theme.spacing(1.5),
      },
    },
    keyboardShortcutListItem: {
      padding: 0,
      display: 'block',
    },
  };
});

const KeyboardShortcutsMenu = ({ className, ...props }) => {
  const classes = useStyles();
  const { ShiftKey, CtrlKey, OptAltKey, CmdWinKey } = useKeyboardKeyLabels();

  const [localSettings] = useState(getLocalSettingsProxy);
  const [useKeyboardShortcuts, setUseKeyboardShortcuts] = useState(
    localSettings.useKeyboardShortcuts,
  );

  const handleKeyboardShortcutsToggle = (isChecked) => {
    localSettings.useKeyboardShortcuts = isChecked;
    setUseKeyboardShortcuts(localSettings.useKeyboardShortcuts);
  };

  const keyboardShortcuts = [
    {
      name: 'Scroll up',
      buttons: [<ArrowDropUp titleAccess="Up Arrow Key" />],
      essential: true,
    },
    {
      name: 'Scroll down',
      buttons: [<ArrowDropDown titleAccess="Down Arrow Key" />],
      essential: true,
    },
    {
      name: NAVIGATION_BUTTONS.NEXT,
      buttons: [OptAltKey, 'N'],
    },
    {
      name: NAVIGATION_BUTTONS.PREVIOUS,
      buttons: [ShiftKey, OptAltKey, 'N'],
    },
    {
      name: 'Access page number input',
      buttons: [OptAltKey, 'P'],
    },
    {
      name: 'Get current location information',
      buttons: [CtrlKey, CmdWinKey, '/'],
    },
    {
      name: 'Focus current book content',
      buttons: [CtrlKey, CmdWinKey, 'B'],
    },
    {
      name: 'Screen readers: create annotation from selection',
      buttons: [CtrlKey, CmdWinKey, 'H'],
    },
    {
      name: 'Toggle on/off bookmark on current page',
      buttons: [ShiftKey, OptAltKey, 'K'],
    },
  ];

  return (
    <div className={clsx(classes.keyboardShortcutsMenu, className)} {...props}>
      <div className={classes.togglePlaceholder}>
        <KeyboardShortcutsToggle
          isEnabled={useKeyboardShortcuts}
          onToggle={(isChecked) => handleKeyboardShortcutsToggle(isChecked)}
        />
      </div>
      <ul className={classes.keyboardShortcuts}>
        {keyboardShortcuts
          .filter((shortcut) =>
            useKeyboardShortcuts ? true : shortcut.essential,
          )
          .map((shortcut) => (
            <ListItemMUI
              key={shortcut.name}
              className={classes.keyboardShortcutListItem}
            >
              <KeyboardShortcut>
                {{
                  name: shortcut.name,
                  buttons: shortcut.buttons,
                }}
              </KeyboardShortcut>
            </ListItemMUI>
          ))}
      </ul>
    </div>
  );
};

KeyboardShortcutsMenu.propTypes = {
  className: PropTypes.string,
};

export default KeyboardShortcutsMenu;
